import { FacetGroup } from '../types';
import { useState, useEffect, useRef } from 'preact/hooks';

interface NumericRangeFacetProps {
  facetGroup: FacetGroup;
  selectedPriceRange: [number, number] | null;
  onPriceRangeChange: (newRange: [number, number]) => void;
}

export function NumericRangeFacet({
  facetGroup,
  selectedPriceRange,
  onPriceRangeChange,
}: NumericRangeFacetProps) {
  const [pendingRange, setPendingRange] = useState<[number, number] | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const DEBOUNCE_DELAY = 300; // milliseconds
  if (
    facetGroup.facet_type.type !== 'numeric' ||
    facetGroup.facet_type.min === undefined ||
    facetGroup.facet_type.max === undefined
  ) {
    return null;
  }

  const possibleMin = facetGroup.facet_type.min;
  const possibleMax = facetGroup.facet_type.max;

  // Initialize selectedPriceRange to possible range if not set
  const currentRange = pendingRange || selectedPriceRange || [possibleMin, possibleMax];

  const selectionMin = Math.max(possibleMin, currentRange[0]);
  const selectionMax = Math.min(possibleMax, currentRange[1]);

  const sliderMin = possibleMin;
  const sliderMax = possibleMax;

  // show nothing if the range is invalid (e.g., only one price point)
  // Use a small epsilon to handle floating point precision issues
  const epsilon = 0.01;
  if (
    sliderMax - sliderMin < epsilon ||
    selectionMax - selectionMin < epsilon
  ) {
    return null;
  }

  // Calculate range bar position and width as percentages
  const range = sliderMax - sliderMin;
  const leftPercent = ((selectionMin - sliderMin) / range) * 100;
  const rightPercent = ((selectionMax - sliderMin) / range) * 100;

  // Clamp percentages to valid range to avoid rendering artifacts
  const clampedLeftPercent = Math.max(0, Math.min(100, leftPercent));
  const clampedRightPercent = Math.max(0, Math.min(100, rightPercent));
  const clampedWidth = clampedRightPercent - clampedLeftPercent;

  // Get histogram data
  const buckets = facetGroup.facet_type.buckets || [];
  const interval = facetGroup.facet_type.interval || 0;
  const maxCount =
    buckets.length > 0 ? Math.max(...buckets.map((b) => b.count)) : 0;

  // Debounce the range change callback
  useEffect(() => {
    if (pendingRange === null) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      onPriceRangeChange(pendingRange);
      debounceTimerRef.current = null;
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [pendingRange, onPriceRangeChange]);

  return (
    <div key={facetGroup.field_name} className="facet-group">
      <h3>{facetGroup.display_name}</h3>
      <div className="price-range-slider">
        {/* Selected range badges on top */}
        <div className="price-range-selected">
          <span className="price-badge price-badge-min">
            {selectionMin.toFixed(0)} €
          </span>
          <span className="price-badge price-badge-max">
            {selectionMax.toFixed(0)} €
          </span>
        </div>

        {/* Histogram visualization */}
        {buckets.length > 0 && (
          <div className="histogram-chart">
            {buckets.map((bucket, idx) => {
              // A bucket is in range if it overlaps with the selection
              // Bucket range: [bucket.key, bucket.key + interval)
              // Selection range: [selectionMin, selectionMax]
              const bucketEnd = bucket.key + interval;
              const isInSelectedRange =
                bucket.key <= selectionMax && bucketEnd >= selectionMin;
              const heightPercent =
                maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;

              return (
                <div key={idx} className="histogram-bar-container">
                  <div
                    className={`histogram-bar ${isInSelectedRange ? 'selected' : ''}`}
                    style={{
                      height: `${heightPercent}%`,
                    }}
                    title={`${bucket.key.toFixed(0)}€ - ${(bucket.key + interval).toFixed(0)}€: ${bucket.count} items`}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Slider controls */}
        <div className="price-slider-container">
          {/* Background track */}
          <div className="slider-track"></div>
          {/* Active range between thumbs */}
          <div
            className="slider-range"
            style={{
              transform: `translate(${clampedLeftPercent}%, -50%) scale(${clampedWidth / 100}, 1)`,
            }}
          ></div>
          <input
            type="range"
            min={sliderMin}
            max={sliderMax}
            value={selectionMin}
            onInput={(e) =>
              setPendingRange([
                parseFloat(e.currentTarget.value),
                currentRange[1],
              ])
            }
            className="price-slider"
          />
          <input
            type="range"
            min={sliderMin}
            max={sliderMax}
            value={selectionMax}
            onInput={(e) =>
              setPendingRange([
                currentRange[0],
                parseFloat(e.currentTarget.value),
              ])
            }
            className="price-slider"
          />
        </div>

        {/* Available range on bottom */}
        <div className="price-range-available">
          <span className="price-label">{possibleMin.toFixed(0)} €</span>
          <span className="price-label price-label-center">
            {((possibleMin + possibleMax) / 2).toFixed(0)} €
          </span>
          <span className="price-label">{possibleMax.toFixed(0)} €</span>
        </div>
      </div>
    </div>
  );
}
